import React, {
  useState,
  useEffect,
  useRef,
  useContext,
  Suspense,
  lazy,
} from 'react';
import get from 'lodash/get';
import { useTranslation } from 'react-i18next';
import { MobileContext } from '@wix/da-shared-react/pkg/Context';
import OnScrollToElement from '@wix/da-shared-react/pkg/OnScrollToElement';
import ErrorBoundary from '@wix/da-shared-react/pkg/ErrorBoundary';
import LoadingIndicator from '@wix/da-shared-react/pkg/LoadingIndicator';
import { useIsMounted } from '@wix/da-shared-react/pkg/utils/hooks/useIsMounted';
import { useFlag } from '@wix/da-shared-react/pkg/redux/flags/hooks';
import withSectionFetchSpinner from '../../../decorators/withSectionFetchSpinner';
import WideWidgetLayout from '../../Layout/WideWidgetLayout';
import StatsSideNavBar from './StatsSideNavBar';
import EmptyState from '../../EmptyState';
import WidgetNavMenu from '../../UserWidgets/_partials/WidgetNavMenu';
import { routes, routesWithRecentVisitors } from './consts';

// Lazy loaded widgets
const OverviewWidget = lazy(
  () => import(/* webpackChunkName: "stats" */ './OverviewWidget')
);
const MySubmissionsWidget = lazy(
  () => import(/* webpackChunkName: "stats" */ './MySubmissionsWidget')
);
const GeneralWidget = lazy(
  () => import(/* webpackChunkName: "stats" */ './GeneralWidget')
);
const TrafficWidget = lazy(
  () => import(/* webpackChunkName: "stats" */ './TrafficWidget')
);
const BenchmarkWidget = lazy(
  () => import(/* webpackChunkName: "stats" */ './BenchmarkWidget')
);

const AllTimeStatsWidget = lazy(
  () => import(/* webpackChunkName: "stats" */ './AllTimeStatsWidget')
);

const RecentVisitorsWidget = lazy(
  () => import(/* webpackChunkName: "stats" */ './RecentVisitors')
);

import profilesCssVars from '../../../styles/variables.scss';
const scrollToChildOffsetTop = parseInt(
  profilesCssVars['navbar-sticky-height']
);

import s from './StatsSection.scss';

export interface Props {
  hasCalculatedStats: boolean;
}

export const StatsSection: React.FC<Props> = ({ hasCalculatedStats }) => {
  const [activeHash, setActiveHash] = useState('overview');
  const isMounted = useIsMounted();
  const isMobile = useContext(MobileContext);
  const widgetRefs = useRef<HTMLDivElement[]>([]);
  const pushWidgetRef = ref => {
    widgetRefs.current.push(ref);
  };
  const withRecentVisistors = useFlag('recent_visitors');

  useEffect(() => {
    const initHash = get(document, 'location.hash').slice(1);
    if (initHash) {
      setActiveHash(initHash);
    }
  }, []);

  const updateActiveHash = (hash: string) => {
    setActiveHash(hash);
    history.replaceState({}, '', `#${hash}`);
  };

  const onScolledToElement = (element: HTMLElement) => {
    updateActiveHash(element.id);
  };

  const { t } = useTranslation();

  if (!isMounted) {
    return null;
  }

  if (!hasCalculatedStats) {
    return (
      <EmptyState
        title={t('pages.stats.empty.title')}
        subtitle={t('pages.stats.empty.subtitle')}
        className={s['empty']}
      />
    );
  }

  const routesWithFlags = withRecentVisistors
    ? routesWithRecentVisitors
    : routes;

  const commonWidgetProps = {
    ref: pushWidgetRef,
    mobileHeaderExtras: <WidgetNavMenu widgetMenuItemsList={routesWithFlags} />,
  };

  return (
    <WideWidgetLayout
      header={t('pages.stats.title')}
      subSubheader={t('pages.stats.subtitle')}
      navigation={
        !isMobile && (
          <StatsSideNavBar
            routes={routesWithFlags}
            activeHash={activeHash}
            updateActiveHash={updateActiveHash}
          />
        )
      }
    >
      <Suspense fallback={LoadingIndicator}>
        <AllTimeStatsWidget id="alltime" {...commonWidgetProps} />
        <OverviewWidget
          id="overview"
          showMobileTimePeriodSwitcherOnlyOnStickyHeader={false}
          {...commonWidgetProps}
        />
        {withRecentVisistors && (
          <RecentVisitorsWidget id="recent_visitors" {...commonWidgetProps} />
        )}
        <MySubmissionsWidget id="my_submissions" {...commonWidgetProps} />
        <TrafficWidget id="traffic" {...commonWidgetProps} />
        <GeneralWidget id="general" {...commonWidgetProps} />
        <BenchmarkWidget id="benchmark" {...commonWidgetProps} />
        <ErrorBoundary hideOnFail>
          <OnScrollToElement
            elements={widgetRefs.current}
            offsetTop={scrollToChildOffsetTop}
            onScrollTo={onScolledToElement}
          />
        </ErrorBoundary>
      </Suspense>
      <div className={s['disclaimer']}>{t('pages.stats.disclaimer')}</div>
    </WideWidgetLayout>
  );
};
StatsSection.displayName = 'StatsSection';

export default withSectionFetchSpinner(StatsSection);
